import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { Loader2, Search, Heart, MessageSquare, Eye, Phone, UserCircle2 } from "lucide-react";

const EVENT_ICON = {
  view: Eye,
  like: Heart,
  unlike: Heart,
  chat: MessageSquare,
};

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
function buildWhatsappUrl(detail) {
  const c = detail.customer;
  const liked = [];
  // Traverse newest → oldest and collect the actual products liked (with names).
  const seen = new Set();
  for (const s of (detail.sessions || [])) {
    for (const ev of (s.events || [])) {
      if (ev.type === "like" && ev.product_id && !seen.has(ev.product_id)) {
        seen.add(ev.product_id);
        liked.push({ name: ev.product_name || "Product", code: ev.product_code });
      }
    }
  }
  const lines = [
    `Hi ${c.name}, thank you for visiting Heda Plywoods today.`,
    "",
    "Here's the wishlist from your visit:",
    ...liked.map((p, i) => `${i + 1}. ${p.name}${p.code ? ` (Code: ${p.code})` : ""}`),
    "",
    "Reply here anytime for quotes, availability or samples.",
    "— Team Heda",
  ];
  const msg = encodeURIComponent(lines.join("\n"));
  // wa.me expects country code prefixed; assume India (+91) if not present.
  const mob = c.mobile.startsWith("+") ? c.mobile : `91${c.mobile}`;
  return `https://wa.me/${mob}?text=${msg}`;
}

  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/showroom/customers");
        if (!cancelled) setCustomers(data || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/showroom/customers/${selected.mobile}`);
        if (!cancelled) setDetail(data);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selected]);

  const filtered = (customers || []).filter((c) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (c.name || "").toLowerCase().includes(q) || (c.mobile || "").includes(q);
  });

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="hed-overline">Showroom</div>
        <h1 className="font-serif text-3xl md:text-4xl mt-1 tracking-tight">
          Customers &amp; their journeys.
        </h1>
        <p className="text-sm text-[#5C5C54] mt-1.5 max-w-2xl">
          Every customer who&rsquo;s used the showroom tablet. Click to see what they liked,
          asked and viewed — across every visit.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        {/* List */}
        <div className="hed-card p-4">
          <div className="relative mb-3">
            <Search size={14} className="absolute left-2.5 top-2.5 text-[#5C5C54]" />
            <input
              data-testid="customer-search"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search name or mobile"
              className="w-full pl-8 pr-2 py-2 text-sm bg-[#FAFAF8] border border-[#E8E6DF] focus:border-[#B5953B] rounded-sm outline-none"
            />
          </div>
          {loading ? (
            <div className="text-xs text-[#5C5C54] italic">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-xs text-[#5C5C54] italic">No customers yet. Start a session to see them here.</div>
          ) : (
            <ul className="space-y-1.5">
              {filtered.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setSelected(c)}
                    data-testid={`customer-row-${c.mobile}`}
                    className={`w-full text-left px-3 py-2 rounded-sm border transition-colors ${
                      selected?.id === c.id
                        ? "bg-[#FDF7E3] border-[#B5953B]"
                        : "bg-white hover:bg-[#FAFAF8] border-[#E8E6DF]"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <UserCircle2 size={14} className="text-[#5E6033] flex-shrink-0" />
                      <span className="font-semibold text-sm truncate flex-1 min-w-0">{c.name}</span>
                      {c.visit_count > 1 && (
                        <span className="text-[10px] uppercase tracking-wider text-[#B5953B]">
                          ×{c.visit_count}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#5C5C54] flex items-center gap-1 mt-0.5">
                      <Phone size={9} /> {c.mobile}
                      <span className="ml-auto">{fmtDate(c.last_seen_at).split(",")[0]}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="hed-card p-5 min-h-[420px]">
          {!selected ? (
            <div className="text-sm text-[#5C5C54] italic">
              Pick a customer to see their full journey.
            </div>
          ) : detailLoading ? (
            <div className="text-sm text-[#5C5C54] inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading history…
            </div>
          ) : !detail ? null : (
            <div>
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div>
                  <div className="font-serif text-2xl tracking-tight">{detail.customer.name}</div>
                  <div className="text-xs text-[#5C5C54] flex items-center gap-1">
                    <Phone size={11} /> {detail.customer.mobile}
                    <span className="ml-3">First seen {fmtDate(detail.customer.first_seen_at)}</span>
                  </div>
                </div>
                <div className="ml-auto inline-flex items-center gap-2 text-xs flex-wrap">
                  <span className="bg-[#FDF7E3] text-[#5E6033] border border-[#B5953B]/30 rounded-sm px-2 py-1">
                    {detail.customer.visit_count} visits
                  </span>
                  <span className="bg-[#FDF7E3] text-[#5E6033] border border-[#B5953B]/30 rounded-sm px-2 py-1 inline-flex items-center gap-1">
                    <Heart size={10} /> {(detail.customer.liked_product_ids || []).length} liked
                  </span>
                  {(detail.customer.liked_product_ids || []).length > 0 && (
                    <a
                      href={buildWhatsappUrl(detail)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="whatsapp-wishlist-button"
                      className="bg-[#25D366] text-white rounded-sm px-2.5 py-1 inline-flex items-center gap-1 hover:bg-[#1FBA57] transition-colors"
                    >
                      <MessageSquare size={11} strokeWidth={2} /> Send wishlist to WhatsApp
                    </a>
                  )}
                </div>
              </div>

              <div className="space-y-5">
                {(detail.sessions || []).map((s) => (
                  <div key={s.id} className="border-l-2 border-[#E8E6DF] pl-4">
                    <div className="text-[11px] uppercase tracking-wider text-[#5C5C54]">
                      Visit · {fmtDate(s.started_at)}
                    </div>
                    <ul className="mt-2 space-y-1.5">
                      {(s.events || []).map((ev, idx) => {
                        const Icon = EVENT_ICON[ev.type] || Eye;
                        const verb = {
                          view: "viewed",
                          like: "liked",
                          unlike: "unliked",
                          chat: "asked about",
                        }[ev.type] || ev.type;
                        return (
                          <li
                            key={idx}
                            className="flex items-start gap-2 text-sm"
                            data-testid={`event-${ev.type}`}
                          >
                            <Icon
                              size={12}
                              className={`mt-1 flex-shrink-0 ${
                                ev.type === "like"
                                  ? "text-[#9E2A2B] fill-[#9E2A2B]"
                                  : "text-[#5E6033]"
                              }`}
                            />
                            <span className="flex-1">
                              <span className="text-[#5C5C54]">{verb}</span>{" "}
                              <span className="font-medium text-[#1A1A18]">
                                {ev.product_name || ev.product_code || "—"}
                              </span>
                              {ev.detail && (
                                <span className="text-[#5C5C54]">: {ev.detail}</span>
                              )}
                            </span>
                            <span className="text-[10px] text-[#5C5C54] whitespace-nowrap">
                              {new Date(ev.ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          </li>
                        );
                      })}
                      {(s.events || []).length === 0 && (
                        <li className="text-xs italic text-[#5C5C54]">No activity recorded.</li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
